import { AIOutput, parseAndCleanJSON, Step, validateAndSanitizeVideoData } from './gemini';
import { validateAndPaginateAllSteps } from './layoutEngine';
import { resolveStepAndLessonImages } from './imageService';
import { UploadedMaterial, StudyMaterialAnalysis, ExamPrepOptions } from './studyMaterials';

export interface StudyMaterialsSummary {
  total_documents: number;
  documents: { fileName: string; role: string }[];
  syllabus_units: { unitTitle: string; topics: string[] }[];
  high_priority_topics: string[];
  source_priority_hierarchy: string[];
  prep_level?: string;
  available_time?: string;
}

export interface RoadmapItem {
  id: string;
  module_number: number;
  phase: string;
  title: string;
  description?: string;
  step_start: number;
  step_end: number;
}

export interface ChapterAnalysis {
  overview: string;
  learning_objectives: string[];
  prerequisites: string[];
  teaching_order: string[];
  major_concepts: string[];
  common_mistakes: string[];
  memory_tricks: string[];
  exam_relevance: string;
}

export interface RevisionSection {
  concept_map: Array<{ concept: string; connects_to: string[]; relationship: string }>;
  formula_sheet: Array<{ name: string; formula: string; description: string }>;
  key_points: string[];
  important_definitions: Array<{ term: string; definition: string }>;
  common_mistakes: string[];
  quick_summary: string;
}

export interface ImportantQuestionItem {
  id: number;
  question: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  type: 'Conceptual' | 'Application' | 'Exam-oriented';
  think_about_this: string;
  approach: string;
  visual_solution?: string;
  answer: string;
  shortcut_key_insight: string;
  common_mistake: string;
}

export interface SmartRecommendations {
  next_chapter: string;
  prerequisites_to_review: string[];
  related_concepts: string[];
  revision_plan: string;
}

export interface MasterFacultyExplanation {
  overview_and_intuition: string;
  core_theories_and_reasoning: string[];
  worked_examples_and_cases: Array<{
    example_title: string;
    problem: string;
    step_by_step_working: string;
    conclusion: string;
  }>;
  exam_questions_master_breakdown: Array<{
    question: string;
    approach: string;
    working: string;
    answer: string;
    key_takeaway: string;
  }>;
  summary_and_synthesis: string;
}

export interface OneShotAIOutput extends AIOutput {
  is_oneshot_mode: true;
  is_study_materials_mode?: boolean;
  subject_info?: {
    class_level?: string;
    board?: string;
  };
  study_materials_summary?: StudyMaterialsSummary;
  master_faculty_explanation?: MasterFacultyExplanation;
  roadmap?: RoadmapItem[];
  chapter_analysis?: ChapterAnalysis;
  revision_section?: RevisionSection;
  important_questions?: ImportantQuestionItem[];
  smart_recommendations?: SmartRecommendations;
}

export function enhanceSvgContrast(svg: string): string {
  if (!svg || typeof svg !== 'string' || !svg.includes('<svg')) return svg;

  let enhanced = svg;

  // Replace dark/black stroke colors with bright high-contrast colors
  enhanced = enhanced.replace(/stroke\s*=\s*["']#(?:000|000000|1e293b|0f172a|334155|111|222|333|black)["']/gi, 'stroke="#38bdf8"');
  enhanced = enhanced.replace(/stroke\s*:\s*#(?:000|000000|1e293b|0f172a|334155|111|222|333|black)/gi, 'stroke: #38bdf8');

  // Replace dark/black fill colors with bright high-contrast colors
  enhanced = enhanced.replace(/fill\s*=\s*["']#(?:000|000000|1e293b|0f172a|334155|111|222|333|black)["']/gi, 'fill="#f8fafc"');
  enhanced = enhanced.replace(/fill\s*:\s*#(?:000|000000|1e293b|0f172a|334155|111|222|333|black)/gi, 'fill: #f8fafc');

  // Ensure <text> tags without explicit fill use bright white fill and bold font
  enhanced = enhanced.replace(/(<text\b(?![^>]*\bfill=)[^>]*>)/gi, '$1 fill="#ffffff" font-weight="bold"');

  return enhanced;
}

export function consolidateMicroScenes(steps: Step[]): Step[] {
  // Pass-through: preservation of granular narration-aligned steps without aggressive merging
  if (!steps || steps.length === 0) return [];
  return steps.map((step, idx) => ({
    ...step,
    step: idx + 1
  }));
}

export async function expandOneShotNarration(
  data: OneShotAIOutput,
  language: string,
  headers: Record<string, string>
): Promise<OneShotAIOutput> {
  const currentWords = data.steps?.reduce((acc, s) => {
    const text = (s.audio_explanation || s.explanation || "").trim();
    return acc + text.split(/\s+/).filter(w => w.length > 0).length;
  }, 0) || 0;

  console.log(`[OneShot Expansion] Checking masterclass depth (${data.steps?.length || 0} scenes, ${currentWords} words)...`);
  if (currentWords >= 2200) return data;

  const expansionPrompt = `
    You are Professor X, master AI teaching professor leading a 20-minute OneShot Learning Masterclass on "${data.topic || 'Masterclass'}".
    Below is a draft JSON structure containing the teaching steps.

    CURRENT DRAFT LESSON STEPS:
    ${JSON.stringify({
      topic: data.topic,
      subject: data.subject,
      steps: data.steps,
      important_questions: data.important_questions
    }, null, 2)}

    EXPANSION MANDATE WITH STRICT TEXT-NARRATION SYNCHRONIZATION:
    The current spoken narration is too brief (${currentWords} words).
    YOU MUST EXPAND the steps array so that each concept, definition, and calculation step has thorough, high-quality spoken narration (50 to 90 words per step).
    - Ensure EVERY step's "board_content" shows only what is currently being explained.
    - Ensure "audio_explanation" and "explanation" match word-for-word in plain speakable text.
    - If a concept has multiple points, split them into sequential steps with their own matching board content.
    - Total spoken words across all steps MUST exceed 2,400 to 2,800 words (~18-22 minutes of speech).

    Return ONLY the full updated JSON matching the exact OneShotAIOutput schema.
  `;

  try {
    const response = await fetch("/api/gemini/generate", {
      method: "POST",
      headers,
      body: JSON.stringify({
        contents: [{ role: 'user', parts: [{ text: expansionPrompt }] }],
        responseMimeType: "application/json"
      }),
    });

    if (!response.ok) {
      console.warn(`[OneShot Expansion] Expansion API call failed with HTTP ${response.status}`);
      return data;
    }

    const resJson = await response.json();
    const expandedParsed = parseAndCleanJSON(resJson.text || "") as OneShotAIOutput;
    if (expandedParsed && expandedParsed.steps && expandedParsed.steps.length > 0) {
      expandedParsed.steps.forEach((step) => {
        if (step.diagram_svg) {
          step.diagram_svg = enhanceSvgContrast(step.diagram_svg);
        }
      });

      return {
        ...data,
        ...expandedParsed,
        is_oneshot_mode: true,
        steps: expandedParsed.steps
      };
    }
    return data;
  } catch (err) {
    console.warn("[OneShot Expansion] Error during expansion pass:", err);
    return data;
  }
}

export async function generateOneShotSolution(
  subject: string,
  classLevel: string,
  board: string,
  chapterTopic: string,
  language: string = 'English',
  studyMaterialsContext?: {
    materials: UploadedMaterial[];
    analysis: StudyMaterialAnalysis;
    options: ExamPrepOptions;
  }
): Promise<OneShotAIOutput> {
  const calculateTotalNarrationDuration = (steps: Step[], lang: string = 'English'): { totalDurationSec: number; totalWords: number; teachingDurationSec: number; questionDurationSec: number } => {
    let totalWords = 0;
    let teachingDurationSec = 0;
    let questionDurationSec = 0;

    const isIndic = /[\u0900-\u097F]/.test(lang) || /Hindi|Bengali|Marathi|Tamil|Telugu/i.test(lang);
    const wordsPerSecond = isIndic ? 1.625 : 2.25;

    steps.forEach((step) => {
      const text = (step.audio_explanation || step.explanation || "").replace(/<[^>]*>/g, '').trim();
      const words = text.split(/\s+/).filter((w) => w.length > 0).length;
      totalWords += words;
      const duration = Math.max(3, words / wordsPerSecond + 1.0);

      if ((step as any).module_number === 7 || (step as any).phase === "Important Questions" || (step as any).is_question) {
        questionDurationSec += duration;
      } else {
        teachingDurationSec += duration;
      }
    });

    return {
      totalDurationSec: teachingDurationSec + questionDurationSec,
      totalWords,
      teachingDurationSec,
      questionDurationSec
    };
  };

  const getStepWords = (s: Step): number => {
    const text = (s.audio_explanation || s.explanation || "").replace(/<[^>]*>/g, '').trim();
    return text.split(/\s+/).filter(w => w.length > 0).length;
  };

  const userApiKey = typeof window !== 'undefined' ? localStorage.getItem('gemini_api_key') : null;
  const googleToken = typeof window !== 'undefined' ? localStorage.getItem('google_access_token') : null;
  const sessionId = typeof window !== 'undefined' ? localStorage.getItem('google_session_id') : null;

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (userApiKey && userApiKey.trim()) {
    headers["x-gemini-api-key"] = userApiKey.trim();
  }
  if (googleToken && googleToken.trim()) {
    headers["x-google-access-token"] = googleToken.trim();
  }
  if (sessionId && sessionId.trim()) {
    headers["x-session-id"] = sessionId.trim();
  }

  const isStudyMode = !!studyMaterialsContext;
  console.log(`[OneShot Multi-Part] Initiating masterclass generation pipeline for: "${chapterTopic}" (${subject}, ${classLevel}) [Study Materials Mode: ${isStudyMode}]`);

  // Helper to fetch JSON from Gemini endpoint
  const callGeminiJson = async (prompt: string, sysInst?: string): Promise<any> => {
    const response = await fetch("/api/gemini/generate", {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
        systemInstruction: sysInst || `You are Professor X, master AI teaching professor leading a 20-minute OneShot Masterclass in ${language}. Return strictly valid JSON.`,
        responseMimeType: "application/json"
      }),
    });

    if (!response.ok) {
      let errText = `API Error: ${response.status}`;
      try {
        const errJson = await response.json();
        errText = errJson.error || errText;
      } catch (e) {}
      throw new Error(errText);
    }

    const resJson = await response.json();
    return parseAndCleanJSON(resJson.text || "");
  };

  try {
    const studyContextPrompt = isStudyMode ? `
      STUDY MATERIALS GROUNDING CONTEXT:
      - Total Uploaded Documents: ${studyMaterialsContext.materials.length}
      - Documents: ${JSON.stringify(studyMaterialsContext.materials.map(m => ({ fileName: m.fileName, role: m.role })))}
      - Syllabus Map: ${JSON.stringify(studyMaterialsContext.analysis.syllabusMap)}
      - High Priority Topics: ${JSON.stringify(studyMaterialsContext.analysis.highPriorityTopics)}
      - Teacher Emphasized Topics: ${JSON.stringify(studyMaterialsContext.analysis.crossDocumentInsights.teacherEmphasizedTopics)}
      - Detected Exam Questions: ${JSON.stringify(studyMaterialsContext.analysis.crossDocumentInsights.detectedQuestions)}
      - Student Prep Level: ${studyMaterialsContext.options.prepLevel}
      - Available Study Time: ${studyMaterialsContext.options.availableTime}

      SOURCE PRIORITY HIERARCHY:
      1. Teacher-provided material / Official syllabus (Primary Truth)
      2. Class notes & assignments
      3. Question banks & previous year papers
      4. Textbook & reference material
      5. Supplementary general AI explanations (only if a syllabus topic is missing from notes)

      MANDATORY GROUNDING RULES:
      - Do NOT alter teacher terminology, definitions, formulas, or methods unnecessarily.
      - Explicitly reference student study materials in step 'reference_text' (e.g. 'Class Notes', 'Syllabus Unit 1', 'Teacher Exam Questions').
      - Teach deeply and intuitively; do NOT merely read summaries.
    ` : '';

    // =========================================================================
    // STAGE 0: MASTER LESSON PLAN
    // =========================================================================
    console.log(`[OneShot Stage 0] Formulating Master Faculty Lesson Plan...`);
    const masterPlanPrompt = `
      You are Professor X, master AI teaching professor planning a structured 20-minute OneShot Learning Masterclass on:
      - Subject: ${subject}
      - Class / Level: ${classLevel || 'Standard Academic Level'}
      - Board / Curriculum: ${board || 'Standard'}
      - Chapter / Topic: ${chapterTopic}
      - Teaching Language: ${language}

      ${studyContextPrompt}

      Design an exhaustive pedagogical master curriculum plan divided into 3 continuous parts:
      - Part 1 (Foundation & Intuition - Modules 1 & 2): Core prerequisites, real-world intuition, primary definitions. Target: 850-1,050 narration words.
      - Part 2 (Deep Conceptual Teaching & Derivations - Modules 3, 4 & 5): In-depth derivations, mechanisms, edge cases, formulas. Target: 850-1,050 narration words.
      - Part 3 (Integration, Exam Questions & Revision - Modules 6 & 7): Synthesis, 4 to 6 fully solved exam questions, recap. Target: 650-850 narration words.

      Return ONLY valid JSON matching this schema:
      {
        "chapter_overview": "Comprehensive overview of ${chapterTopic}...",
        "prerequisites": ["Prerequisite 1", "Prerequisite 2"],
        "major_concepts": ["Concept 1", "Concept 2", "Concept 3", "Concept 4"],
        "common_misconceptions": ["Misconception 1", "Misconception 2"],
        "exam_relevance": "High importance for board/entrance exams...",
        "part1_topics": ["Module 1: Foundation & Intuition", "Module 2: Core Concept & Primary Laws"],
        "part2_topics": ["Module 3: Derivations & Mechanics", "Module 4: Applications & Relationships", "Module 5: Edge Cases & Special Scenarios"],
        "part3_topics": ["Module 6: Complete Integration & Synthesis", "Module 7: Important Exam Questions & Step-by-Step Solving"],
        "practice_questions": [
          "Exam Question 1 (Easy/Conceptual)",
          "Exam Question 2 (Medium/Application)",
          "Exam Question 3 (Medium/Derivation)",
          "Exam Question 4 (Hard/Exam-oriented)",
          "Exam Question 5 (Hard/Multi-step)"
        ]
      }
    `;

    let plan = await callGeminiJson(masterPlanPrompt);
    console.log(`[OneShot Stage 0] Master Plan generated successfully. Overview length: ${plan?.chapter_overview?.length || 0}`);

    // =========================================================================
    // STAGE 1: PART 1 GENERATION (Modules 1 & 2)
    // =========================================================================
    console.log(`[OneShot Stage 1] Generating Part 1 (Foundation & Intuition - Modules 1 & 2)...`);
    const part1Prompt = `
      You are Professor X leading PART 1 of the 20-minute OneShot Masterclass on "${chapterTopic}".
      Target: PART 1 — FOUNDATION & CORE INTUITION (Modules 1 & 2).

      MASTER CURRICULUM PLAN FOR PART 1:
      ${JSON.stringify({ overview: plan.chapter_overview, topics: plan.part1_topics, prerequisites: plan.prerequisites }, null, 2)}

      MANDATORY REQUIREMENTS FOR PART 1:
      1. Generate 4 to 6 substantial, multi-point teaching steps for Modules 1 & 2.
      2. Step 1 MUST ALWAYS start with: "Hello, I am Professor X..." (translated into ${language}).
      3. Total spoken narration across Part 1 steps MUST BE BETWEEN 850 and 1,050 WORDS (~7-8 minutes of spoken lecture time).
      4. MANDATORY TWO-LAYER STRUCTURE FOR EVERY STEP:
         - FACULTY NARRATION ("audio_explanation" & "explanation"): Exhaustive 160 to 220 word plain-text faculty lecture narrative explaining intuition, definitions, and real-world examples in depth.
         - NO raw LaTeX (\\\\frac, \\\\sqrt, $...), NO raw brackets, NO markdown symbols inside "audio_explanation" or "explanation". Write speakable words.
         - "explanation" (English) and "audio_explanation" (spoken in ${language}) MUST BE IDENTICAL IN CONTENT so captions match spoken audio word-for-word.
         - BOARD CONTENT ("board_content"): MUST NOT BE A COPY OF THE NARRATION PARAGRAPH! Do NOT output walls of raw text on the board.
         - "board_content" MUST contain concise, clean, structured visual teaching material (3 to 6 lines max) using UPPERCASE HEADINGS, bullet points ("• "), numbered steps ("1."), LaTeX formulas ($$...$$), or markdown tables (| Col A | Col B |).
      5. INTELLIGENT CONCEPT VISUALS MANDATE:
         - If the concept introduced in Module 1 or 2 benefits significantly from a visual representation (e.g., Quadratic Equations -> Parabola intersecting x-axis at roots; Physics -> Coordinate frame/trajectory; Geometry -> Core geometric construction; Biology -> Labeled cell/organ structure), provide a clean, high-contrast SVG diagram in 'diagram_svg' (viewBox="0 0 500 350", stroke="#38bdf8", fill="none", labels fill="#ffffff", font-weight="bold") or set 'image_query' at the exact teaching moment.
         - For Quadratic Equations: create an accurate SVG parabola showing coordinate axes (X and Y), parabola curve y=ax^2+bx+c, x-intercepts highlighted and labeled as 'Roots / Zeros (where f(x)=0)', and vertex.

      Include "subject_info", "chapter_analysis", "master_faculty_explanation", "roadmap" (7 items total covering all 7 modules), and "steps" for Modules 1 & 2.

      Return valid JSON with structure:
      {
        "subject": "${subject}",
        "topic": "${chapterTopic}",
        "question": "OneShot Masterclass: ${chapterTopic}",
        "subject_info": { "class_level": "${classLevel}", "board": "${board}" },
        "chapter_analysis": {
          "overview": "${plan.chapter_overview || ''}",
          "learning_objectives": ["..."],
          "prerequisites": ${JSON.stringify(plan.prerequisites || [])},
          "teaching_order": ${JSON.stringify(plan.major_concepts || [])},
          "major_concepts": ${JSON.stringify(plan.major_concepts || [])},
          "common_mistakes": ${JSON.stringify(plan.common_misconceptions || [])},
          "memory_tricks": ["..."],
          "exam_relevance": "${plan.exam_relevance || ''}"
        },
        "master_faculty_explanation": {
          "overview_and_intuition": "Deep intuitive introduction...",
          "core_theories_and_reasoning": ["Theory 1...", "Theory 2..."],
          "worked_examples_and_cases": [],
          "exam_questions_master_breakdown": [],
          "summary_and_synthesis": "Part 1 foundation summary..."
        },
        "roadmap": [
          { "id": "1", "module_number": 1, "phase": "Module 1", "title": "Module 1: Foundation & Intuition", "description": "Core intuition and prerequisites", "step_start": 1, "step_end": 3 },
          { "id": "2", "module_number": 2, "phase": "Module 2", "title": "Module 2: Core Concepts & Principles", "description": "Primary definitions and fundamental laws", "step_start": 4, "step_end": 6 },
          { "id": "3", "module_number": 3, "phase": "Module 3", "title": "Module 3: Deeper Derivations & Formulas", "description": "Mathematical and logical rigor", "step_start": 7, "step_end": 10 },
          { "id": "4", "module_number": 4, "phase": "Module 4", "title": "Module 4: Practical Applications", "description": "Real-world relationships and mechanics", "step_start": 11, "step_end": 13 },
          { "id": "5", "module_number": 5, "phase": "Module 5", "title": "Module 5: Advanced Scenarios & Special Cases", "description": "Complex cases and edge condition mechanics", "step_start": 14, "step_end": 16 },
          { "id": "6", "module_number": 6, "phase": "Module 6", "title": "Module 6: Complete Integration & Revision", "description": "Comprehensive synthesis and formula recap", "step_start": 17, "step_end": 18 },
          { "id": "7", "module_number": 7, "phase": "Important Questions", "title": "5-Min Exam Questions Session", "description": "Representative solved exam questions", "step_start": 19, "step_end": 24 }
        ],
        "steps": [
          {
            "step": 1,
            "module_number": 1,
            "board_content": "...",
            "explanation": "Hello, I am Professor X...",
            "audio_explanation": "Hello, I am Professor X...",
            "reference_text": "Module 1: Foundation",
            "diagram_svg": ""
          }
        ]
      }
    `;

    let part1 = await callGeminiJson(part1Prompt);
    let part1Words = part1.steps?.reduce((acc: number, s: Step) => acc + getStepWords(s), 0) || 0;
    console.log(`[OneShot Stage 1] Part 1 generated: ${part1.steps?.length || 0} steps, ${part1Words} words.`);

    // =========================================================================
    // STAGE 2: PART 2 GENERATION (Modules 3, 4 & 5)
    // =========================================================================
    console.log(`[OneShot Stage 2] Generating Part 2 (Deep Conceptual Teaching - Modules 3, 4 & 5)...`);
    const part1Summary = part1.steps?.map((s: Step) => `Step ${s.step} (Module ${(s as any).module_number}): ${s.audio_explanation?.slice(0, 100)}...`).join('\n');

    const part2Prompt = `
      You are Professor X leading PART 2 of the 20-minute OneShot Masterclass on "${chapterTopic}".
      Target: PART 2 — DEEP CONCEPTUAL TEACHING, DERIVATIONS & APPLICATIONS (Modules 3, 4 & 5).

      PREVIOUS PART 1 COMPLETED STEPS SUMMARY:
      ${part1Summary}

      MASTER CURRICULUM PLAN FOR PART 2:
      ${JSON.stringify({ topics: plan.part2_topics, misconceptions: plan.common_misconceptions }, null, 2)}

      CONTINUITY & MANDATORY REQUIREMENTS FOR PART 2:
      1. Continue seamlessly from Part 1. Use natural faculty transitions ("Now that we understand...", "Building directly on our previous principle..."). Do NOT repeat Part 1 introductions!
      2. Generate 5 to 7 substantial, multi-point teaching steps for Modules 3, 4 & 5. Set "module_number" accordingly (3, 4, or 5).
      3. Total spoken narration across Part 2 steps MUST BE BETWEEN 850 and 1,050 WORDS (~7-8 minutes of continuous spoken lecture time).
      4. MANDATORY TWO-LAYER STRUCTURE FOR EVERY STEP:
         - FACULTY NARRATION ("audio_explanation" & "explanation"): Exhaustive 160 to 220 word plain-text faculty lecture narrative explaining derivations, mechanisms, and edge cases in depth.
         - NO raw LaTeX or markdown code in "audio_explanation" or "explanation". Plain speakable words only. Identical across explanation & audio_explanation.
         - BOARD CONTENT ("board_content"): MUST NOT BE A COPY OF THE NARRATION PARAGRAPH! Do NOT output raw walls of narration text.
         - "board_content" MUST contain concise, clean, structured visual teaching material (3 to 6 lines max) using UPPERCASE HEADINGS, bullet points ("• "), numbered derivation steps ("1."), LaTeX formulas ($$...$$), or comparison tables (| Concept A | Concept B |).
      5. MULTIPLE INTELLIGENT VISUALS SUPPORT:
         - When progressing through new subtopics in Modules 3, 4, or 5 (e.g. Discriminant cases $D > 0, D = 0, D < 0$ in quadratics, Projectile trajectory, Free-Body Diagram with vectors, Circuit diagrams, or Chemical reaction pathways), supply a new, dedicated high-contrast SVG diagram in 'diagram_svg' or 'image_query'.
         - Visuals must appear sequentially at the exact moment the concept is explained, with clear labels and educational purpose.

      Return valid JSON with key:
      {
        "steps": [
          {
            "step": 7,
            "module_number": 3,
            "board_content": "...",
            "explanation": "...",
            "audio_explanation": "...",
            "reference_text": "Module 3: Derivations",
            "diagram_svg": ""
          }
        ]
      }
    `;

    let part2 = await callGeminiJson(part2Prompt);
    let part2Words = part2.steps?.reduce((acc: number, s: Step) => acc + getStepWords(s), 0) || 0;
    console.log(`[OneShot Stage 2] Part 2 generated: ${part2.steps?.length || 0} steps, ${part2Words} words.`);

    // =========================================================================
    // STAGE 3: PART 3 GENERATION (Modules 6 & 7)
    // =========================================================================
    console.log(`[OneShot Stage 3] Generating Part 3 (Integration, Practice Questions & Revision - Modules 6 & 7)...`);
    const part2Summary = part2.steps?.map((s: Step) => `Step ${s.step} (Module ${(s as any).module_number}): ${s.audio_explanation?.slice(0, 100)}...`).join('\n');

    const part3Prompt = `
      You are Professor X leading PART 3 of the 20-minute OneShot Masterclass on "${chapterTopic}".
      Target: PART 3 — CHAPTER INTEGRATION, EXAM QUESTIONS & REVISION (Modules 6 & 7).

      PREVIOUS PARTS COMPLETED SUMMARY:
      ${part2Summary}

      MASTER CURRICULUM PLAN FOR PART 3 & PRACTICE QUESTIONS:
      ${JSON.stringify({ topics: plan.part3_topics, practice_questions: plan.practice_questions }, null, 2)}

      CONTINUITY & MANDATORY REQUIREMENTS FOR PART 3:
      1. Continue seamlessly into Module 6 (Integration & Synthesis) and Module 7 (Important Exam Questions).
      2. Generate 4 to 6 substantial teaching steps for Module 6 (Revision/Synthesis) and Module 7 (4 to 6 fully solved exam questions).
      3. CRITICAL MANDATE FOR IMPORTANT QUESTIONS / PYQ SOLUTIONS:
         - For EVERY important question taught in Module 7 and in the "important_questions" array:
         - Provide a COMPLETE, EDUCATIONAL, STEP-BY-STEP BREAKDOWN.
         - NEVER jump directly from the question to the final answer.
         - Show ALL intermediate steps:
           * Given parameters & variable definitions (e.g. Let speed be $x\\text{ km/h}$)
           * Initial conceptual equation (e.g. $\\text{Time}_1 - \\text{Time}_2 = 1 \\implies \\frac{360}{x} - \\frac{360}{x+5} = 1$)
           * Algebraic expansion & common denominator transformation (e.g. $\\frac{360(x+5) - 360x}{x(x+5)} = 1 \\implies \\frac{1800}{x^2+5x} = 1$)
           * Standard quadratic equation form ($x^2 + 5x - 1800 = 0$)
           * Middle-term splitting or Quadratic Formula factorization ($x^2 + 45x - 40x - 1800 = 0 \\implies x(x+45) - 40(x+45) = 0 \\implies (x-40)(x+45) = 0$)
           * Root identification and physical domain check ($x = 40$ or $x = -45$; Speed cannot be negative, so reject $x = -45$)
           * Clear final conclusion statement ($\text{Speed of the train} = 40\\text{ km/h}$)
         - If a solution requires substantial working, split it cleanly across 2 sequential teaching steps (e.g. Step A: Equation Setup & Reduction; Step B: Factorization & Root Interpretation) to respect single-frame layout budget (max 3-4 clean math lines per frame).
         - Spoken narration must explain each intermediate calculation and transformation with complete teaching clarity.
      4. MANDATORY TWO-LAYER STRUCTURE FOR EVERY STEP:
         - FACULTY NARRATION ("audio_explanation" & "explanation"): Exhaustive 160 to 220 word plain-text faculty lecture narrative explaining the synthesis and step-by-step problem-solving. Speakable plain words only.
         - BOARD CONTENT ("board_content"): MUST NOT BE A COPY OF THE NARRATION PARAGRAPH! Concise, clean, structured visual teaching material (3 to 6 lines max) using UPPERCASE HEADINGS, bullet points ("• "), numbered steps ("1."), formulas ($$...$$), or tables (| Col 1 | Col 2 |).
      5. Total spoken narration across Part 3 steps MUST BE BETWEEN 650 and 850 WORDS (~5-6 minutes of spoken lecture time).
      6. Generate the "important_questions" array containing 4 to 6 fully structured practice items matching the exam questions taught. Each item's 'answer' field MUST contain the full detailed step-by-step working.
      7. Include "revision_section", "formulas", "smart_recommendations", and "final_answer".

      Return valid JSON with key:
      {
        "steps": [ ... steps for module 6 and module 7 questions ... ],
        "important_questions": [
          {
            "id": 1,
            "question": "...",
            "difficulty": "Easy",
            "type": "Conceptual",
            "think_about_this": "...",
            "approach": "...",
            "answer": "...",
            "shortcut_key_insight": "...",
            "common_mistake": "..."
          }
        ],
        "revision_section": {
          "concept_map": [ { "concept": "...", "connects_to": ["..."], "relationship": "..." } ],
          "formula_sheet": [ { "name": "...", "formula": "...", "description": "..." } ],
          "key_points": ["..."],
          "important_definitions": [ { "term": "...", "definition": "..." } ],
          "common_mistakes": ["..."],
          "quick_summary": "..."
        },
        "formulas": ["Formula 1", "Formula 2"],
        "smart_recommendations": {
          "next_chapter": "Next recommended topic...",
          "prerequisites_to_review": ["..."],
          "related_concepts": ["..."],
          "revision_plan": "..."
        },
        "final_answer": "Complete OneShot 6-Chapter Masterclass Completed: ${chapterTopic}"
      }
    `;

    let part3 = await callGeminiJson(part3Prompt);
    let part3Words = part3.steps?.reduce((acc: number, s: Step) => acc + getStepWords(s), 0) || 0;
    console.log(`[OneShot Stage 3] Part 3 generated: ${part3.steps?.length || 0} steps, ${part3Words} words, ${part3.important_questions?.length || 0} questions.`);

    // =========================================================================
    // STAGE 4: MERGE, CONTINUITY CHECK & EXPANSION IF NEEDED
    // =========================================================================
    console.log(`[OneShot Stage 4] Merging Parts 1, 2, and 3...`);

    const allStepsRaw: Step[] = [
      ...(part1.steps || []),
      ...(part2.steps || []),
      ...(part3.steps || [])
    ];

    // Renumber steps sequentially
    let mergedSteps = allStepsRaw.map((step, idx) => ({
      ...step,
      step: idx + 1
    }));

    if (mergedSteps.length > 20) {
      mergedSteps = consolidateMicroScenes(mergedSteps);
    }

    // Enhance SVG contrast
    mergedSteps.forEach((s) => {
      if (s.diagram_svg) {
        s.diagram_svg = enhanceSvgContrast(s.diagram_svg);
      }
    });

    let mergedOutput: OneShotAIOutput = {
      ...part1,
      is_oneshot_mode: true,
      is_study_materials_mode: isStudyMode,
      study_materials_summary: isStudyMode && studyMaterialsContext ? {
        total_documents: studyMaterialsContext.materials.length,
        documents: studyMaterialsContext.materials.map(m => ({ fileName: m.fileName, role: m.role })),
        syllabus_units: studyMaterialsContext.analysis.syllabusMap.map(u => ({ unitTitle: u.unitTitle, topics: u.topics })),
        high_priority_topics: studyMaterialsContext.analysis.highPriorityTopics,
        source_priority_hierarchy: studyMaterialsContext.analysis.sourcePrioritySummary,
        prep_level: studyMaterialsContext.options.prepLevel,
        available_time: studyMaterialsContext.options.availableTime
      } : undefined,
      steps: mergedSteps,
      important_questions: part3.important_questions || part1.important_questions || [],
      revision_section: part3.revision_section || part1.revision_section,
      formulas: part3.formulas || part1.formulas || [],
      smart_recommendations: part3.smart_recommendations || part1.smart_recommendations,
      final_answer: part3.final_answer || `Complete OneShot 6-Chapter Masterclass Completed: ${chapterTopic}`
    };

    let durationInfo = calculateTotalNarrationDuration(mergedOutput.steps || [], language);
    console.log(`[OneShot Stage 4] Merged Masterclass stats: ${mergedOutput.steps?.length || 0} total steps, ${durationInfo.totalWords} spoken narration words (~${Math.round(durationInfo.totalDurationSec / 60)} minutes).`);

    // If total words are under 2,200 words, run targeted expansion on the merged output
    if (durationInfo.totalWords < 2200) {
      console.warn(`[OneShot Stage 4] Merged narration (${durationInfo.totalWords} words) is below 2,200 words threshold. Running targeted Expansion Pass...`);
      mergedOutput = await expandOneShotNarration(mergedOutput, language, headers);

      if (mergedOutput.steps && mergedOutput.steps.length > 20) {
        mergedOutput.steps = consolidateMicroScenes(mergedOutput.steps);
      }

      durationInfo = calculateTotalNarrationDuration(mergedOutput.steps || [], language);
      console.log(`[OneShot Stage 4] Post-expansion Masterclass stats: ${mergedOutput.steps?.length || 0} steps, ${durationInfo.totalWords} spoken words (~${Math.round(durationInfo.totalDurationSec / 60)} minutes).`);
    }

    // Sanitize steps to ensure structured board content and synchronized narration
    mergedOutput = validateAndSanitizeVideoData(mergedOutput);

    // Deterministically paginate all steps to strictly enforce dynamic text-narration alignment
    if (mergedOutput.steps && mergedOutput.steps.length > 0) {
      mergedOutput.steps = validateAndPaginateAllSteps(mergedOutput.steps, 'landscape');
    }

    // Resolve visuals via imageService
    await resolveStepAndLessonImages(mergedOutput);

    console.log(`[OneShot Complete] Masterclass successfully generated and verified! (${mergedOutput.steps?.length || 0} steps, ${mergedOutput.important_questions?.length || 0} practice questions).`);
    return mergedOutput;

  } catch (error: any) {
    console.error("[OneShot Multi-Part] Error during OneShot Learning generation:", error);
    throw error;
  }
}

